import { props2BoolSchema, props2NumberArraySchema, props2NumberSchema, props2TextArraySchema, props2TextSchema, } from "@msys/props2";
import z from "zod";
import { ApplyItemActionContingentSchema, ApplyItemActionDecisionSchema, ApplyItemActionPlaceholderForTemplateSchema, ApplyitemActionProductSchema, ApplyitemActionPropsSchema, ApplyItemActionRecommendationsSchema, } from "@msys/apply-item-action-isomorphic";
const askIfShape = { askIfExpr: z.string() };
/**
 * A non-computed prop plus its ask-if condition, re-inlined from the document
 * logic layer. The wizard evaluates `askIfExpr` client-side to decide which of
 * a question's properties to show, so the condition must travel with the prop.
 */
export const QuestionPropSchema = z.union([
    props2TextSchema.extend(askIfShape),
    props2NumberSchema.extend(askIfShape),
    props2BoolSchema.extend(askIfShape),
    props2NumberArraySchema.extend(askIfShape),
    props2TextArraySchema.extend(askIfShape),
]);
export const AttachmentSchema = z.strictObject({
    id: z.string(),
    url: z.string(),
    title: z.string(),
    mimeType: z.string(),
});
export const BreadcrumbSchema = z.strictObject({
    itemId: z.string(),
    itemTitle: z.string(),
});
export const AskWhenSchema = z.union([
    z.literal("onQuoteCreate"),
    z.literal("onQuoteRefinement"),
    z.literal("onQuoteRefinement2"),
    z.literal("onQuoteRefinement3"),
    z.literal("onEmbeddableWizard"),
    z.literal("onCallAssistant"),
]);
export const AskWhomSchema = z.union([
    z.literal("contractee"),
    z.literal("contractor"),
]);
export const QuestionStrategySchema = z.discriminatedUnion("type", [
    z.strictObject({
        type: z.literal("questionControl"),
        askWhom: z.enum(["contractee", "contractor"]),
        // .optional()
        // .default("contractee"),
        askWhen: z.enum([
            "onEmbeddableWizard",
            "onQuoteCreate",
            "onQuoteRefinement",
            "onQuoteRefinement2",
            "onQuoteRefinement3",
            "onCallAssistant",
        ]),
        // optional for backwards compatibility, can be removed after all clients are updated.
        // .optional()
        // .default("onEmbeddableWizard"),
        startingNode: z.union([
            z.strictObject({
                type: z.literal("root"),
            }),
            z.strictObject({
                type: z.literal("itemId"),
                itemId: z.string(),
            }),
        ]),
        // .optional(),
    }),
    z.strictObject({
        type: z.literal("item"),
        itemId: z.string(),
    }),
    z.strictObject({
        type: z.literal("item-each"),
    }),
    // z.strictObject({
    //   type: z.literal("processCompletion"),
    //   startingNode: z
    //     .union([
    //       z.strictObject({
    //         type: z.literal("root"),
    //       }),
    //       z.strictObject({
    //         type: z.literal("itemId"),
    //         itemId: z.string(),
    //       }),
    //     ])
    //     .optional(),
    //   processId: z.string(),
    // }),
    z.strictObject({
        type: z.literal("inferred"),
        role: z.union([
            z.literal("agent"),
            z.literal("sales"),
            z.literal("finance"),
            z.literal("vorort"),
            z.literal("homeowner"),
        ]),
        phase: z.union([
            z.literal("lead"),
            z.literal("quote_unqualified"),
            z.literal("quote_qualified"),
            z.literal("aufmass"),
        ]),
        expertiseLevel: z.union([
            z.literal("beginner"),
            z.literal("intermediate"),
            z.literal("expert"),
        ]),
        importance: z.coerce.number(),
        tags: z.string().array(),
        startingNode: z
            .union([
            z.strictObject({
                type: z.literal("root"),
            }),
            z.strictObject({
                type: z.literal("itemId"),
                itemId: z.string(),
            }),
        ])
            .optional(),
    }),
]);
const QuestionControlSchema = z
    .object({
    askWhen: AskWhenSchema.array(),
    askWhom: AskWhomSchema.array(),
})
    .array();
const __PropNonComputedBaseSchema = z.strictObject({
    key: z.string(),
    label: z.string(),
    essential: z.boolean(),
    group: z.string(),
    clientVisibility: z.boolean(),
    prompt: z.string(),
    questionControl: QuestionControlSchema,
    askIfExpr: z.string(),
    customizable: z.boolean(),
});
const __PropComputedBaseSchema = z
    .object({
    key: z.string(),
    label: z.string(),
    group: z.string(),
    clientVisibility: z.boolean(),
    expr: z.string(),
})
    // apps/backend serve2: [serve-source-watch-build]         "message": "[\n  {\n    \"code\": \"unrecognized_keys\",\n    \"keys\": [\n      \"aliasedExpr\",\n      \"missingValue\",\n      \"valueTextComputed\"\n    ],\n    \"path\": [\n      \"allDocItems\",\n      0,\n      \"$props\",\n      1\n    ],\n    \"message\": \"Unrecognized key(s) in object: 'aliasedExpr', 'missingValue', 'valueTextComputed'\"\n  }\n]",
    .strip();
export const PropComputedSchema = z.discriminatedUnion("kind", [
    __PropComputedBaseSchema.extend({
        kind: z.literal("Props2TextComputed"),
    }),
    __PropComputedBaseSchema.extend({
        kind: z.literal("Props2TextArrayComputed"),
    }),
    __PropComputedBaseSchema.extend({
        kind: z.literal("Props2BoolComputed"),
    }),
    __PropComputedBaseSchema.extend({
        kind: z.literal("Props2NumberComputed"),
        unit: z.string().nullable(),
    }),
    __PropComputedBaseSchema.extend({
        kind: z.literal("Props2NumberArrayComputed"),
        unit: z.string().nullable(),
    }),
]);
const AllowedValueMediaSchema = z.object({
    id: z.string(),
    url: z.string(),
    title: z.string(),
    mimeType: z.string(),
});
const AllowedValueTextSchema = z.object({
    allowedText: z.string(),
    media: AllowedValueMediaSchema.nullable(),
});
const AllowedValueNumberSchema = z.object({
    allowedNumber: z.number(),
    media: AllowedValueMediaSchema.nullable(),
});
const ValueMediaBoolSchema = z.object({
    bool: z.boolean(),
    media: AllowedValueMediaSchema,
});
export const PropNonComputedSchema = z.discriminatedUnion("kind", [
    __PropNonComputedBaseSchema.extend({
        kind: z.literal("Props2Bool"),
        valueBool: z.boolean().nullable(),
        valuesMediaBool: ValueMediaBoolSchema.array(),
    }),
    __PropNonComputedBaseSchema.extend({
        kind: z.literal("Props2Text"),
        valueText: z.string().nullable(),
        allowedValuesText: AllowedValueTextSchema.array(),
    }),
    __PropNonComputedBaseSchema.extend({
        kind: z.literal("Props2TextArray"),
        valueTextArray: z.string().array().nullable(),
        allowedValuesText: AllowedValueTextSchema.array(),
    }),
    __PropNonComputedBaseSchema.extend({
        kind: z.literal("Props2Number"),
        valueNumber: z.number().nullable(),
        range: z
            .strictObject({
            min: z.number().nullable(),
            max: z.number().nullable(),
        })
            .nullable(),
        unit: z.string().nullable(),
        step: z.number().nullable(),
        allowedValuesNumber: AllowedValueNumberSchema.array(),
    }),
    __PropNonComputedBaseSchema.extend({
        kind: z.literal("Props2NumberArray"),
        valueNumberArray: z.number().array().nullable(),
        range: z
            .strictObject({
            min: z.number().nullable(),
            max: z.number().nullable(),
        })
            .nullable(),
        unit: z.string().nullable(),
        step: z.number().nullable(),
        allowedValuesNumber: AllowedValueNumberSchema.array(),
    }),
]);
export const PropSchema = z.union([PropComputedSchema, PropNonComputedSchema]);
export const AskIfRelatedDataSchema = z.object({
    allDocItems: z
        .object({
        id: z.string(),
        $props: PropSchema.array(),
    })
        .array(),
});
// -----------
export const SalutationSchema = z.union([z.literal("mr"), z.literal("ms")]);
export const AddressSchema = z.strictObject({
    city: z.string(),
    postalCode: z.string(),
    countryCode: z.string(),
    streetLines1: z.string(),
    location: z
        .object({
        lat: z.number(),
        lng: z.number(),
    })
        .nullable(),
});
export const ProductSchema = z.strictObject({
    id: z.string(),
    articleNumber: z.string(), // TODO: needed as identifier. To be obsfucated
    supplierId: z.string(),
    title: z.string(),
    description: z.string(),
    marketingText: z.string(),
    attachments: z.array(AttachmentSchema),
    listPrice: z.number().nullable(),
    currency: z.string(),
    branding: z
        .strictObject({
        brand: z.strictObject({ name: z.string() }),
        brandLine: z.strictObject({ name: z.string() }).nullable(),
    })
        .nullable(),
});
export const ContractorSettingsSchema = z.strictObject({
    isLeadAddressRequired: z.boolean(),
});
export const PhoneType = z.enum([
    "WORK",
    "HOME",
    "MOBILE",
    "PRIVATE",
    "HOTLINE",
]);
export const PhoneSchema = z.strictObject({
    type: PhoneType,
    number: z.string(),
});
export const Meister1Flow = z.strictObject({
    label: z.string(),
    identName: z.string(),
    calculatorCategoryCraftId: z.string().optional(),
    calculatorId: z.number(),
    id: z.string(),
});
export const OpeningHoursSchema = z.strictObject({
    from: z.string().nullable(),
    to: z.string().nullable(),
    isOpen: z.boolean(),
});
export const ContractorSchema = z.strictObject({
    id: z.string(),
    name: z.string(),
    yearFounded: z.number().nullable(),
    employeesNumber: z.number().nullable(),
    openingHours: z.array(OpeningHoursSchema).nullable(),
    contactPhones: z.array(PhoneSchema),
    contactEmail: z.string().nullable(),
    contactWebsite: z.string().nullable(),
    contactAddress: AddressSchema.nullable(),
    categories: z.array(z.string()).nullable(),
    settings: ContractorSettingsSchema,
    meister1Flows: z.array(Meister1Flow),
});
export const SourceDocSchema = z.union([
    z.object({
        type: z.literal("quote"),
        quoteId: z.string(),
        contractorId: z.string(),
    }),
    z.object({
        type: z.literal("requirement"),
        requirementId: z.string(),
        owningOrganisationId: z.string(),
    }),
    z.object({
        type: z.literal("template"),
        templateId: z.string(),
        contractorId: z.string(),
    }),
    z.object({
        type: z.literal("template-latest-available"),
        templateId: z.string(),
        contractorId: z.string(),
    }),
    z.object({
        type: z.literal("template-unpublished"),
        templateId: z.string(),
        contractorId: z.string(),
    }),
]);
// ---------
const __QuestionBaseSchema = z.strictObject({
    id: z.string(),
    questionId: z.string(),
    key: z.string(),
    itemId: z.string(),
    itemTitle: z.string(),
    breadcrumbs: BreadcrumbSchema.array(),
    dimensions: z.strictObject({
        requiredExpertise: z.union([
            z.literal("beginner"),
            z.literal("intermediate"),
            z.literal("expert"),
        ]),
        relevantPhases: z
            .union([
            z.literal("lead"),
            z.literal("quote_unqualified"),
            z.literal("quote_qualified"),
            z.literal("aufmass"),
        ])
            .array(),
        allowedRoles: z
            .union([
            z.literal("agent"),
            z.literal("sales"),
            z.literal("finance"),
            z.literal("vorort"),
            z.literal("homeowner"),
        ])
            .array(),
        importance: z.number(),
    }),
    dependencies: z
        .strictObject({
        questionId: z.string(),
        type: z.union([z.literal("requires"), z.literal("recommends")]),
    })
        .array(),
    tags: z.string().array(),
});
export const QuestionSchema = z.discriminatedUnion("type", [
    __QuestionBaseSchema.extend({
        type: z.literal("contingency"),
        media: z.array(AttachmentSchema),
        preselected: z.boolean(),
        prompt: z.string(),
        answer: ApplyItemActionContingentSchema.nullable(),
    }),
    __QuestionBaseSchema.extend({
        type: z.literal("multiple-properties"),
        props: QuestionPropSchema.array(),
        answer: ApplyitemActionPropsSchema.nullable(),
    }),
    __QuestionBaseSchema.extend({
        type: z.literal("decision"),
        options: z
            .strictObject({
            itemId: z.string(),
            title: z.string(),
            description: z.string(),
            price: z.number().nullable(),
            currency: z.string(),
            media: z.array(AttachmentSchema),
            preselected: z.boolean(),
        })
            .array(),
        behavior: z.union([z.literal("many"), z.literal("one")]),
        prompt: z.string(),
        answer: ApplyItemActionDecisionSchema.nullable(),
    }),
    __QuestionBaseSchema.extend({
        type: z.literal("product"),
        productTypeId: z.string(),
        productTypeTitle: z.string(),
        prompt: z.string(),
        answer: ApplyitemActionProductSchema.nullable(),
        currentProduct: z
            .strictObject({
            supplierId: z.string(),
            articleNumber: z.string(),
        })
            .nullable(),
    }),
    __QuestionBaseSchema.extend({
        type: z.literal("multiple-recommendations"),
        recommendations: z
            .discriminatedUnion("type", [
            z.strictObject({
                type: z.literal("recommendation-type"),
                templateTypeId: z.string(),
                templateTypeTitle: z.string(),
                templateTypeIdAtRevision: z.number(),
                templateTypeDescription: z.string(),
                templateTypePrice: z.number().nullable(),
                templateTypeCurrency: z.string(),
                templateTypeMedia: z.array(AttachmentSchema),
                included: z.boolean(),
            }),
            z.strictObject({
                type: z.literal("recommendation-template"),
                templateId: z.string(),
                templateTitle: z.string(),
                templateDescription: z.string(),
                templatePrice: z.number().nullable(),
                templateCurrency: z.string(),
                templateMedia: z.array(AttachmentSchema),
                included: z.boolean(),
            }),
        ])
            .array(),
        prompt: z.string(),
        answer: ApplyItemActionRecommendationsSchema.nullable(),
    }),
    __QuestionBaseSchema.extend({
        type: z.literal("placeholder-for-template"),
        placeholderForTemplateTypeIdAtRevision: z.number(),
        placeholderForTemplateTypeId: z.string(),
        placeholderForTemplateTypeTitle: z.string(),
        defaultTemplateId: z.string().nullable(),
        prompt: z.string(),
        answer: ApplyItemActionPlaceholderForTemplateSchema.nullable(),
    }),
]);
export const TemplateSchema = z.strictObject({
    id: z.string(),
    title: z.string(),
    description: z.string(),
    wizardDescription: z.string(), // html!
    pictureUrl: z.string().nullable(),
    attachments: z.array(AttachmentSchema),
    priceNetTotal: z.number().nullable(),
});
export const TemplateListSchema = z.strictObject({
    id: z.string(),
    title: z.string(),
});
//# sourceMappingURL=question-types-and-schema.js.map